import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { AddressDisplay } from "../../components/AddressForm";
import { useAuth } from "../../providers/authprovider";
import findColorForUser from "./CalendarUtils";
import getDateForInputFromMysqlDate from "../../utils/utils";
import PickWorkerSelect from "./PickWorkerSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

export default function AddEditEventModal(props) {
  const EMPTY_EVENT = {
    id: undefined,
    type: "MALFUNCTION",
    date: props.date,
    fvat: false,
    fvat_submit: false,
    fvat_description: "",
    time: "",
    frequency: undefined,
    building_id: undefined,
    info: "",
    users: [],
  };

  const { privilege, workers, teams, buildings } = props;
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const parseEvent = ({
    id,
    type,
    offers,
    date,
    fvat,
    fvat_submit,
    fvat_description,
    building_id,
    info,
    users,
    team_id,
    duration,
    time,
    frequency,
  }) => {
    const e = {
      id: id,
      type: type,
      date: date,
      time: time,
      fvat: fvat,
      fvat_submit: fvat_submit,
      fvat_description: fvat_description,
      duration: duration,
      building_id: building_id,
      info: info,
      users: [],
      offers: offers ? [...offers] : [],
      frequency: frequency,
    };

    if (id) e.users = workers.filter((w) => users.includes(w.id));
    else if (team_id) {
      e.users = selectTeamWorkers(
        teams.find((t) => t.id === team_id),
        workers,
      );
    }

    return e;
  };
  const { calendar } = useAuth();
  const [formData, setFormData] = useState(() =>
    props.event !== undefined ? parseEvent(props.event) : EMPTY_EVENT,
  );
  const [invoiceFiles, setInvoiceFiles] = useState({
    zleceniePdf: null,
    innePdf: null,
  });
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const [validated] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    content: "",
    variant: "",
    title: "",
  });
  const EDITION = formData.id !== undefined;

  const handleSelectBuilding = (options) => {
    if (options.length <= 0)
      return setFormData({ ...formData, building_id: undefined });

    let { id } = options[0];

    setFormData({ ...formData, building_id: id });
  };

  const hideAlert = () => {
    setAlert({
      show: false,
      content: "",
      variant: "",
      title: "",
    });
  };

  function getBuildingAddress(building_id) {
    const building = buildings.find((b) => b.id === building_id);

    if (!building) return;

    return (
      <div>
        <b>Adres: </b>
        <div className={"ms-4"}>
          <AddressDisplay address={building} />
        </div>
      </div>
    );
  }

  const handleSelectTeam = (event) => {
    const teamId = event.target.value;

    if (teamId.length <= 0) {
      return;
    }

    const team = teams.find((t) => t.id === teamId);

    const teamWorkers = selectTeamWorkers(team, workers);

    setFormData({ ...formData, ["users"]: teamWorkers });
  };

  const handlePickWorker = (workerId) => {
    const user = workers.find((w) => w.id === workerId);
    const worker = formData.users.find((u) => u.id === workerId);

    if (user === undefined) return;

    if (worker === undefined)
      setFormData({ ...formData, users: [...formData.users, user] });
  };

  const handleDeleteWorker = (workerId) => {
    setFormData({
      ...formData,
      users: [...formData.users.filter((u) => u.id !== workerId)],
    });
  };

  const handleFvatCheckboxChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.checked });
  };

  const handleInvoiceFileChange = (event) => {
    const file = event.target.files?.[0];
    const { name } = event.target;

    if (!file) {
      setInvoiceFiles({ ...invoiceFiles, [name]: null });
      return;
    }

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      event.target.value = "";
      setAlert({
        show: true,
        content: "Można dodać tylko plik PDF.",
        variant: "warning",
        title: "Błąd",
      });
      return;
    }

    setInvoiceFiles({ ...invoiceFiles, [name]: file });
  };

  const SAVE_DISABLED = formData.building_id === undefined;

  const handleSave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (privilege.edit === false) return;

    const data = {
      ...formData,
      confirmed: true,
      date: new Date(formData.date).getTime(),
      users: formData.users.map((u) => u.id),
    };

    const shouldSendFiles = formData.fvat || formData.fvat_submit;
    let requestData = data;

    if (shouldSendFiles) {
      const multipartData = new FormData();
      multipartData.append("data", JSON.stringify(data));

      if (invoiceFiles.zleceniePdf) {
        multipartData.append("zlecenie", invoiceFiles.zleceniePdf);
      }

      if (invoiceFiles.innePdf) {
        multipartData.append("inne", invoiceFiles.innePdf);
      }

      requestData = multipartData;
    }

    calendar.add(requestData, EDITION, (response) => {
      const { success, error, content } = response;

      if (success === true) {
        props.onSuccess(content);
        props.onHide();
        return;
      }

      setAlert({
        show: true,
        content: error,
        variant: "warning",
        title: "Błąd",
      });
    });
  };

  const defaultInputValue = () => {
    if (formData.building_id === undefined) return "";

    let b = buildings.find((bu) => bu.id === formData.building_id);

    if (b === undefined) return "";

    return b.name;
  };

  const handleAddOffer = (offer) => {
    const offers = formData.offers ? formData.offers : [];

    const index = offers.findIndex((o) => o.id === offer.id);

    if (index > -1) return;

    offers.push(offer);

    setFormData({ ...formData, offers: [...offers] });
  };

  const handleRemoveOffer = (offerId) => {
    if (!formData.offers) return;

    const offers = formData.offers;

    const index = offers.findIndex((o) => o.id === offerId);

    if (index < 0) return;

    offers.splice(index, 1);

    setFormData({ ...formData, offers: [...offers] });
  };

  function isInspection() {
    return formData.type === "INSPECTION";
  }

  function isTraining() {
    return formData.type === "TRAINING";
  }

  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      {formData && (
        <Form
          action={"post"}
          onSubmit={handleSave}
          noValidate
          validated={validated}
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              <Form.Select
                size={"lg"}
                defaultValue={formData.type}
                disabled={isInspection()}
                name={"type"}
                aria-label="Select event type"
                onChange={handleChange}
              >
                <option value={"INSPECTION"}>Inspekcja</option>
                <option value={"MALFUNCTION"}>Zgłoszenie/Awaria</option>
                <option value={"TRAINING"}>Szkolenie</option>
              </Form.Select>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert
              show={alert.show}
              variant={alert.variant}
              onClose={hideAlert}
              dismissible
            >
              <p>{alert.content}</p>
            </Alert>
            <Row>
              <Col lg={4}>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="team_select">Data</InputGroup.Text>
                  <Form.Control
                    defaultValue={getDateForInputFromMysqlDate(props.date)}
                    type="date"
                    name={"date"}
                    placeholder="data wydarzenia"
                    onChange={handleChange}
                  />
                </InputGroup>
              </Col>
              {isInspection() && (
                <Col lg={8}>
                  <InputGroup className="mb-3">
                    <InputGroup.Text id="team_select">
                      Częstotliwość
                    </InputGroup.Text>
                    <Form.Select
                      defaultValue={formData.frequency}
                      name={"frequency"}
                      aria-label="Default select example"
                      onChange={handleChange}
                    >
                      <option value={12}>miesięczny</option>
                      <option value={4}>kwartalny</option>
                      <option value={2}>półroczny</option>
                      <option value={1}>roczny</option>
                      <option value={0.33}>trzyletni</option>
                      <option value={0.2}>pięcioletni</option>
                      <option value={0}>jednorazowy</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
              )}
              <Col lg={6}>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="event_duration">
                    Czas trwania
                  </InputGroup.Text>
                  <Form.Control
                    defaultValue={formData.duration}
                    min={0}
                    type="number"
                    name={"duration"}
                    placeholder=""
                    onChange={handleChange}
                  />
                  <InputGroup.Text id="event_duration">dni</InputGroup.Text>
                </InputGroup>
              </Col>
              {isTraining() && (
                <Col lg={4}>
                  <InputGroup className="mb-3">
                    <InputGroup.Text id="team_select">Godzina</InputGroup.Text>
                    <Form.Control
                      defaultValue={formData.time}
                      type="time"
                      name={"time"}
                      placeholder="godzina wydarzenia"
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Col>
              )}
            </Row>

            <Form.Group>
              <Form.Label as={"h5"}>Obiekt</Form.Label>
              <div className={"ms-4"}>
                <Typeahead
                  disabled={isInspection()}
                  id="select_building_auto"
                  labelKey="name"
                  onChange={handleSelectBuilding}
                  options={buildings}
                  placeholder="Wybierz obiekt..."
                  defaultInputValue={defaultInputValue()}
                />
                {formData.building_id &&
                  getBuildingAddress(formData.building_id)}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label as={"h5"}>Pracownicy</Form.Label>
              <div className={"ms-4"}>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="team_select">
                    Wybierz zespół
                  </InputGroup.Text>
                  <Form.Select
                    aria-label="Default select example"
                    onChange={handleSelectTeam}
                  >
                    <option></option>
                    {teams.map((t) => createTeamOptionForSelect(t))}
                  </Form.Select>
                </InputGroup>
                <Table size={"sm"} bordered hover>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Imię i nazwisko</th>
                      <th>Numer tel</th>
                      <th>Stanowisko</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.users.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        teams={teams}
                        onDelete={handleDeleteWorker}
                      />
                    ))}
                  </tbody>
                </Table>
                <Row>
                  <Col lg={6}></Col>
                  <Col lg={6}>
                    <PickWorkerSelect
                      workers={workers.filter((worker) => worker.active)}
                      withButton={true}
                      onPick={handlePickWorker}
                    />
                  </Col>
                </Row>
              </div>
            </Form.Group>
            {isAdmin && (
              <>
                {/* OFERTY */}
                <Form.Group>
                  <Form.Label as={"h5"}>Oferty</Form.Label>
                  <div className={"ms-4"}>
                    <TableOffers
                      eventId={formData.id}
                      buildingFilter={formData.building_id}
                      onAddOffer={handleAddOffer}
                      offers={formData.offers}
                      onRemove={handleRemoveOffer}
                    />
                  </div>
                </Form.Group>
                {/* Faktury */}
                <Form.Group className={"mt-2"}>
                  <Form.Label as={"h5"}>Faktura</Form.Label>
                  <div className={"ms-4"}>
                    <Form.Check
                      inline
                      type={"checkbox"}
                      id={`fvat_check`}
                      name={"fvat"}
                      onChange={handleFvatCheckboxChange}
                      label={`wystawiono fakturę`}
                      checked={formData.fvat}
                    />
                    <Form.Check
                      inline
                      type={"checkbox"}
                      id={`fvat_submit_check`}
                      name={"fvat_submit"}
                      onChange={handleFvatCheckboxChange}
                      label={`wysłano do fakturacji`}
                      checked={formData.fvat_submit}
                    />

                    {formData.fvat || formData.fvat_submit ? (
                      <>
                        <Form.Control
                          as={"textarea"}
                          name={"fvat_description"}
                          onChange={handleChange}
                          placeholder="opis faktury"
                          defaultValue={formData.fvat_description}
                        />
                        <Row className="mt-2">
                          <Col lg={6}>
                            <Form.Label>Zlecenie (PDF)</Form.Label>
                            <Form.Control
                              type="file"
                              name="zleceniePdf"
                              accept="application/pdf,.pdf"
                              onChange={handleInvoiceFileChange}
                            />
                          </Col>
                          <Col lg={6}>
                            <Form.Label>Inne (PDF)</Form.Label>
                            <Form.Control
                              type="file"
                              name="innePdf"
                              accept="application/pdf,.pdf"
                              onChange={handleInvoiceFileChange}
                            />
                          </Col>
                        </Row>
                      </>
                    ) : null}
                  </div>
                </Form.Group>
              </>
            )}
            <Form.Group className={"mt-2"}>
              <Form.Label as={"h5"}>Dodatkowe informacje</Form.Label>
              <div className={"ms-4"}>
                <Form.Control
                  as={"textarea"}
                  name={"info"}
                  onChange={handleChange}
                  placeholder="dodatkowe informacje dotyczące zadania"
                  defaultValue={formData.info}
                />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {privilege.edit === true ? (
              <Button
                disabled={SAVE_DISABLED}
                type={"submit"}
                onClick={handleSave}
              >
                Zapisz
              </Button>
            ) : (
              <Button
                type={"button"}
                variant={"outline-info"}
                onClick={() => props.onHide()}
              >
                Zamknij
              </Button>
            )}
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}

function TableOffers(props) {
  const buildingId = props.buildingFilter;
  const {
    offers: { getBuildingOffers, download },
    viewPdf,
  } = useAuth();
  const offers = props.offers;
  const inputElement = useRef();
  const [buildingOffers, setBuildingOffers] = useState({});
  const [isInvalid, setIsInvalid] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const autocompleteOffers = useMemo(() => {
    if (buildingId === undefined) return [];

    return buildingOffers[buildingId] ? buildingOffers[buildingId] : [];
  }, [buildingOffers]);

  useEffect(() => {
    if (buildingId === undefined) return;

    if (buildingOffers[buildingId] !== undefined) return;

    getBuildingOffers(buildingId, ({ success, error, content }) => {
      if (!success) return;

      setBuildingOffers({ ...buildingOffers, [buildingId]: content });
    });
  });

  const showOffer = (id) => {
    setDownloading(true);
    download(id, ({ success, error, content }) => {
      setDownloading(false);
      if (success) return viewPdf(content);
    });
  };

  const handleSelectOffer = () => {
    const offerNumber = inputElement.current.value;

    const offer = buildingOffers[buildingId]?.find(
      (offer) => offer.nr === offerNumber,
    );

    setIsInvalid(!offer);

    if (!offer) return;

    props.onAddOffer(offer);

    inputElement.current.value = "";
  };

  const removeOfferFromEvent = (offerId) => {
    props.onRemove(offerId);
  };

  return (
    <>
      <Table bordered size={"sm"}>
        <thead>
          <tr>
            <th>L.p.</th>
            <th>Numer</th>
            <th>Nazwa</th>
            <th>Wystawiajacy</th>
            <th>Podgląd</th>
            <th>Usuń</th>
          </tr>
        </thead>
        <tbody>
          {offers?.map((offer, i) => {
            return (
              <tr key={offer.id}>
                <td>{i + 1}</td>
                <td>{offer.nr}</td>
                <td>{offer.name}</td>
                <td>{offer.user}</td>
                <td>
                  <Button
                    disabled={downloading}
                    variant={"outline-success"}
                    onClick={() => showOffer(offer.id)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                </td>
                <td>
                  <Button
                    variant={"outline-secondary"}
                    onClick={() => removeOfferFromEvent(offer.id)}
                    className={"btn-close"}
                  ></Button>{" "}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className={"d-flex justify-content-end"}>
        <InputGroup className={"w-75 "} hasValidation>
          <InputGroup.Text>Wybierz: </InputGroup.Text>
          <Form.Control
            ref={inputElement}
            list={"offers"}
            required
            id={"offer_id"}
            isInvalid={isInvalid}
          />
          <Button variant={"secondary"} onClick={handleSelectOffer}>
            Dodaj
          </Button>
          <Form.Control.Feedback type="invalid">
            Wybrano nieprawidłową ofertę
          </Form.Control.Feedback>
        </InputGroup>
        <datalist id="offers">
          {autocompleteOffers?.map((a) => {
            const name = a.name ? <b>{a.name}</b> : "";
            return (
              <option key={a.id} value={`${a.nr}`}>
                {name} wystawiono: {a.offer_date}
              </option>
            );
          })}
        </datalist>
      </div>
    </>
  );
}

function createTeamOptionForSelect(team) {
  const { leader, technician1, technician2 } = team;

  let inner = team.number + ". ";

  if (leader) inner += leader.name;

  if (technician1) inner += "," + technician1.name;

  if (technician2) inner += "," + technician2.name;

  return (
    <option key={team.id} value={team.id}>
      {inner}
    </option>
  );
}

function UserRow({ user, teams, onDelete }) {
  const userColor = findColorForUser(user.id, teams);

  const handleDeletePerson = (id) => {
    onDelete(id);
  };

  return (
    <tr className={user.active ? "" : "worker-inactive"}>
      <td>
        <div
          className={"userColor"}
          style={{ backgroundColor: userColor }}
        ></div>
      </td>
      <td>{user.name}</td>
      <td>{user.tel}</td>
      <td>{user.position}</td>
      <td>
        <span
          className={"fs-5 close-btn"}
          onClick={() => {
            handleDeletePerson(user.id);
          }}
          aria-hidden={"true"}
        >
          &times;
        </span>
      </td>
    </tr>
  );
}

function selectTeamWorkers(team, workers) {
  if (team === undefined) return [];

  const { leader, technician1, technician2 } = team;

  let ids = [];
  if (leader) ids.push(leader.id);
  if (technician1) ids.push(technician1.id);
  if (technician2) ids.push(technician2.id);

  return workers.filter((w) => ids.includes(w.id));
}
